import { ArticleType } from './article.types'
import { CarePerson } from './carePerson.types'
import { Nullish } from './common.types'
import { DateRangeParam, ReportRequestWithDate } from './reports.types'
import { BaseTableRequest } from './table.types'
import { UserUpdater } from './user.types'

/** Mirrors backend `ECareTeamType`; values must stay byte-identical to the API contract. */
export enum TeamType {
  LearningTeam = 'learning_team',
  PersonalSupportTeam = 'personal_support_team',
  PersonCenteredTeam = 'person_centered_team',
  CustomizedTeam = 'customized_team',
}

export const TeamTypeLabels: Record<TeamType, string> = {
  [TeamType.LearningTeam]: 'Learning Team',
  [TeamType.PersonalSupportTeam]: 'Personal Support Team',
  [TeamType.PersonCenteredTeam]: 'Person-Centered Team',
  [TeamType.CustomizedTeam]: 'Customized Team',
}

export const TeamTypeAbbreviations: Record<TeamType, string> = {
  [TeamType.LearningTeam]: 'LT',
  [TeamType.PersonalSupportTeam]: 'PST',
  [TeamType.PersonCenteredTeam]: 'PCT',
  [TeamType.CustomizedTeam]: 'CT',
}

/** Team type with its abbreviation, e.g. `Learning Team (LT)`. */
export function formatTeamTypeWithAbbreviation(type: TeamType): string {
  const label = TeamTypeLabels[type]
  if (!label) return type

  const abbreviation = TeamTypeAbbreviations[type]
  return abbreviation ? `${label} (${abbreviation})` : label
}

export const TeamTypeOptions = Object.values(TeamType).map((value) => ({
  label: formatTeamTypeWithAbbreviation(value),
  value,
}))

export const PERSON_CENTERED_TEAM_HINT =
  'Use the First Name and Last Name of the Person of Concern.'

/** Team switcher option, e.g. `LT – Memory Unit A`. */
export function formatTeamSelectLabel(team: Pick<Team, 'name' | 'type'>): string {
  const abbreviation = TeamTypeAbbreviations[team.type]
  return abbreviation ? `${abbreviation} – ${team.name}` : team.name
}

/** Full label for Teams tables, e.g. `Customized Team (CT) — Research Group`. */
export function formatTeamTypeLabel(
  type: TeamType,
  customTypeDescription?: Nullish<string>,
): string {
  if (!TeamTypeLabels[type]) return type

  const label = formatTeamTypeWithAbbreviation(type)
  return type === TeamType.CustomizedTeam && customTypeDescription
    ? `${label} — ${customTypeDescription}`
    : label
}

export type TeamCreateRequest = {
  name: string
  type: TeamType
  /** Required when `type` is `customized_team`; cleared by the API for every other type. */
  customTypeDescription?: string
}

export interface Team {
  isDefault: boolean
  isActive: boolean
  isSystemManaged: boolean
  id: number
  createdBy: number
  ownerId: number
  name: string
  type: TeamType
  customTypeDescription?: Nullish<string>
  accessCodeId: number
  /** Person this care team is for; POC name in the UI should follow the active team, not only `user.personOfConcern*`. */
  carePersonId?: Nullish<number>
  carePerson?: CarePerson | null
  updatedAt: Nullish<string>
  createdAt: string
  updatedBy: Nullish<number>
  deletedAt: Nullish<number>
}

export type TeamUpdateRequest = Partial<Team>

/** Channel(s) the invitation was sent through when a member is added. */
export type InviteSentVia = 'email' | 'sms' | 'both' | 'neither'

export interface TeamMember {
  id: number
  careTeamId: number
  userId: Nullish<number>
  email: string
  firstName: string
  lastName: string
  phone: string
  accessCodeId: number
  address: string
  role: Nullish<string[]>
  otherRole: string
  about: string
  user: Nullish<UserTeamMember>
  addressStreet?: string
  addressZipCode?: string
  addressCountry?: string
  addressCity?: string
  addressState?: string
  comments?: string
  createdAt: string
  creator: Nullish<UserUpdater>
  /** Present on the add-member response: how the invitation was delivered. */
  inviteSentVia?: InviteSentVia
}

export interface UserTeamMember
  extends Omit<TeamMember, 'firstName' | 'lastName' | 'user' | 'phone'> {
  firstname: string
  lastname: string
  phoneNumber: string
}

export interface TeamMembersRequest extends BaseTableRequest {
  id: number
}

export interface TeamMemberCreateRequest {
  id: number
  userId: number
  email: string
  firstName: string
  lastName: string
  about: string
  role: string[]
  otherRole?: string
  street: string
  zipCode: string
  country: string
  city: string
  state: string
  phone: string
  comments: string
}

export type TeamMemberUpdateRequest = {
  id: number
  memberId: number
} & Partial<{
  firstName: string
  lastName: string
  email: string
  phone: string
  role: string[]
  otherRole: string
  about: string
  addressStreet: string
  addressZipCode: string
  addressCountry: string
  addressCity: string
  addressState: string
  comments: string
}>

export interface TeamMemberRemoveRequest {
  id: number // Team ID
  memberId: number
}

export enum TraineeTeamRole {
  directCareGiver = 'directCareGiver',
  supervisor = 'supervisor',
  activityDirector = 'activityDirector',
  physicalTherapist = 'physicalTherapist',
  dietaryStaff = 'dietaryStaff',
  custodialStaff = 'custodialStaff',
  leadFamilyCarePartner = 'leadFamilyCarePartner',
  nearestFamilyMember = 'nearestFamilyMember',
  medicalPowerOfAttorney = 'medicalPowerOfAttorney',
  financialPowerOfAttorney = 'financialPowerOfAttorney',
  spouseOrPartner = 'spouseOrPartner',
  child = 'child',
  grandchild = 'grandchild',
  otherFamilyRelation = 'otherFamilyRelation',
  friend = 'friend',
  other = 'other',
}

export const professionalCaregiverRoleOptions = [
  {
    label: 'Direct caregiver',
    value: TraineeTeamRole.directCareGiver,
  },
  {
    label: 'Supervisor',
    value: TraineeTeamRole.supervisor,
  },
  {
    label: 'Activities director',
    value: TraineeTeamRole.activityDirector,
  },
  {
    label: 'Physical therapist',
    value: TraineeTeamRole.physicalTherapist,
  },
  {
    label: 'Dietary staff',
    value: TraineeTeamRole.dietaryStaff,
  },
  {
    label: 'Custodial staff',
    value: TraineeTeamRole.custodialStaff,
  },
  {
    label: 'Other',
    value: TraineeTeamRole.other,
  },
]

export const familyCaregiverRoleOptions = [
  {
    label: 'Lead care partner',
    value: TraineeTeamRole.leadFamilyCarePartner,
    description: 'the coordinator for family interactions',
  },
  {
    label: 'Medical power of attorney',
    value: TraineeTeamRole.medicalPowerOfAttorney,
    description: 'has legal authority for medical decisions',
  },
  {
    label: 'Financial power of attorney',
    value: TraineeTeamRole.financialPowerOfAttorney,
    description: 'has legal authority for financial decisions',
  },
  {
    label: 'Located closest',
    value: TraineeTeamRole.nearestFamilyMember,
  },
  {
    label: 'Spouse or partner',
    value: TraineeTeamRole.spouseOrPartner,
  },
  {
    label: 'Child',
    value: TraineeTeamRole.child,
  },
  {
    label: 'Grandchild',
    value: TraineeTeamRole.grandchild,
  },
  {
    label: 'Other family relation',
    value: TraineeTeamRole.otherFamilyRelation,
  },
  {
    label: 'Friend',
    value: TraineeTeamRole.friend,
  },
  {
    label: 'Other',
    value: TraineeTeamRole.other,
  },
]

export const allCaregiverRoles = [
  ...professionalCaregiverRoleOptions,
  ...familyCaregiverRoleOptions,
]

/** @deprecated Use allCaregiverRoles */
export const traineeAllRoles = allCaregiverRoles

/** @deprecated Use professionalCaregiverRoleOptions */
export const traineeRoleOptions = professionalCaregiverRoleOptions

export interface TeamInfoMember {
  id: number
  firstName: string
  lastName: string
  email: string
  role: Nullish<string[]>
  otherRole: string
  creator: Nullish<UserUpdater>
  createdAt: string
}

export interface TeamInfo {
  id: number
  name: string
  isSystemManaged: boolean
  ownerId: number
  ownerFirstname: string
  ownerLastname: string
  ownerEmail: string
  type: TeamType
  customTypeDescription?: Nullish<string>
  accessCodeId: number
  accessCodeCode: string
  createdBy: number
  updatedBy: number
  updatedAt: number
  createdAt: string
  organizationId: number
  organizationName: string
  members: TeamInfoMember[]
}

export interface ShortTeamInfo {
  id: number
  name: string
  membersCount: number
  createdAt: string
  invitedBy: Nullish<UserUpdater>
}

export interface TeamShareRequest {
  userIds: number[]
  careTeamId: number
  comment: string
  entityId: number
  type: ArticleType | 'note'
}

export interface SharingTimelineRequest {
  userId: number
  teamId: number
}

export type TeamCreateInOrganizationRequest = {
  name: string
  type: TeamType
  /** Required when `type` is `customized_team`; cleared by the API for every other type. */
  customTypeDescription?: string
  organizationId: number | null
  accessCodeId: number
  memberIds: number[]
}

export enum AdminCareTeamsScope {
  familyCaregiver = 'familyCaregiver',
}

export type AdminCareTeamsParams = Partial<DateRangeParam> & {
  scope?: AdminCareTeamsScope
}

export type AdminCareTeamsRequest = Omit<ReportRequestWithDate, 'params'> & {
  params?: AdminCareTeamsParams
}
